import { SYSTEM_VIEWS, type WorkspaceViewDataset } from '@parcel/filters';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  toast,
} from '@parcel/ui';
import { List, Plus, Star, X } from 'lucide-react';
import * as React from 'react';
import { ApiError } from '../../lib/api.js';
import {
  DATASET_SELECTION_PLURAL,
  type MembershipOutcome,
  membershipOutcomeMessage,
  reconcileViewSelection,
} from '../../lib/view-selection-state.js';
import type { WorkspaceView } from '../../lib/workspace-views.js';

export interface ViewSelectionToolbarProps {
  dataset: WorkspaceViewDataset;
  selectedIds: readonly string[];
  staticViews: readonly WorkspaceView[];
  activeStaticView: WorkspaceView | null;
  pending?: boolean;
  onAdd(viewId: string): Promise<MembershipOutcome[]>;
  onCreate(name: string): Promise<{
    view: WorkspaceView;
    outcomes: MembershipOutcome[];
  }>;
  onRemove?(): Promise<MembershipOutcome[]>;
  /** Follow the selection: add it to the workspace's Following View (accounts and projects). */
  onFollow?(): Promise<{ view: WorkspaceView | null; outcomes: MembershipOutcome[] }>;
  onOpenView(view: WorkspaceView): void;
  onSelectionChange(ids: readonly string[]): void;
  /** Optional controlled open state (also used by tests). */
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
  /** Dataset actions shown after the View actions (contacts: Access emails). */
  extraActions?: React.ReactNode;
}

function viewErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const body = err.body as { error?: { message?: unknown } };
    if (typeof body.error?.message === 'string') return body.error.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Success copy for an add/create. Reports what the server actually wrote: creating a
 * Static View truncates the selection, so the selected count would overstate it.
 */
export function membershipSuccessMessage(
  outcomes: readonly MembershipOutcome[],
  plural: string,
  viewName: string,
): string {
  return `${countOutcomes(outcomes).succeeded} ${plural} are now in ${viewName}`;
}

/** Success copy for a removal. Removal never truncates, but it can still partially fail. */
export function membershipRemovalMessage(
  outcomes: readonly MembershipOutcome[],
  plural: string,
  viewName: string,
): string {
  return `${countOutcomes(outcomes).succeeded} ${plural} removed from ${viewName}`;
}

function countOutcomes(outcomes: readonly MembershipOutcome[]) {
  let succeeded = 0;
  let failed = 0;
  for (const outcome of outcomes) {
    if (outcome.status === 'ok') succeeded += 1;
    else failed += 1;
  }
  return { succeeded, failed };
}

/** Same-dataset Static Views only; Dynamic Views never appear, and the Following View has its own entry. */
export function compatibleStaticViews(views: readonly WorkspaceView[]): WorkspaceView[] {
  return views.filter((view) => view.view_type === 'static' && !view.system_kind);
}

export function createStaticViewDialogCopy(
  count: number,
  dataset: WorkspaceViewDataset,
): { title: string; description: string; submitLabel: string } {
  return {
    title: 'Create Static View',
    description: `${count} selected ${DATASET_SELECTION_PLURAL[dataset]}`,
    submitLabel: 'Create View',
  };
}

/** Presentational Command menu — SSR-tested; mounted inside PopoverContent in production. */
export function ViewSelectionAddCommand({
  staticViews,
  pending,
  onSelectView,
  onCreateNew,
  followLabel,
  onFollow,
}: {
  staticViews: readonly WorkspaceView[];
  pending?: boolean;
  onSelectView?: (view: WorkspaceView) => void;
  onCreateNew?: () => void;
  /** The Following View's name; shown first when onFollow is set. */
  followLabel?: string;
  onFollow?: () => void;
}) {
  const views = compatibleStaticViews(staticViews);
  const followGroup =
    onFollow && followLabel ? (
      <CommandGroup heading="Follow">
        <CommandItem forceMount value={followLabel} disabled={pending} onSelect={() => onFollow()}>
          <Star className="size-4" />
          {followLabel}
        </CommandItem>
      </CommandGroup>
    ) : null;

  // Nothing to search or pick from: a search box over an empty list and an empty
  // "Static Views" heading are noise. Collapse to the one action that does something.
  if (views.length === 0) {
    return (
      <Command>
        <CommandList>
          {followGroup}
          <CommandGroup>
            <CommandItem disabled={pending} onSelect={() => onCreateNew?.()}>
              <Plus className="size-4" />
              Create new Static View
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    );
  }

  return (
    <Command>
      <CommandInput placeholder="Search Static Views…" />
      <CommandList>
        <CommandEmpty>No Static Views found.</CommandEmpty>
        {followGroup}
        <CommandGroup heading="Static Views">
          {views.map((view) => (
            <CommandItem
              key={view.id}
              value={view.name}
              disabled={pending}
              onSelect={() => onSelectView?.(view)}
            >
              <List className="size-4" />
              {view.name}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        {/*
          forceMount: cmdk filters items by their text, and this one reads "Create new
          Static View". Typing the name of a View that does not exist yet is exactly
          when you want to create it, and is exactly the text that matches nothing, so
          without this the action disappears at the moment it is needed. Filtering stays
          on for the View list above; a forceMount item is not counted toward the empty
          state, so "No Static Views found." still shows alongside it.
        */}
        <CommandItem forceMount disabled={pending} onSelect={() => onCreateNew?.()}>
          <Plus className="size-4" />
          Create new Static View
        </CommandItem>
      </CommandList>
    </Command>
  );
}

/** Presentational create dialog body — SSR-tested; mounted inside DialogContent. */
export function CreateStaticViewDialogBody({
  dataset,
  selectedCount,
  name,
  onNameChange,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  dataset: WorkspaceViewDataset;
  selectedCount: number;
  name: string;
  onNameChange: (value: string) => void;
  pending?: boolean;
  error?: string | null;
  onCancel?: () => void;
  onSubmit?: () => void;
}) {
  const copy = createStaticViewDialogCopy(selectedCount, dataset);
  return (
    <>
      <DialogHeader>
        <DialogTitle>{copy.title}</DialogTitle>
        <DialogDescription>{copy.description}</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="static-view-name">Name</Label>
        <Input
          id="static-view-name"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          // Enter submits, like InlineViewNameInput on the View tabs. There is no form
          // element here, so without this the only affordance is the mouse. Escape is
          // left to the Dialog, which already closes on it.
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            if (!pending && name.trim().length > 0) onSubmit?.();
          }}
          placeholder="View name"
          disabled={pending}
          autoFocus
        />
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={pending} onClick={() => onCancel?.()}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={pending || name.trim().length === 0}
          onClick={() => onSubmit?.()}
        >
          {copy.submitLabel}
        </Button>
      </div>
    </>
  );
}

export function ViewSelectionToolbar({
  dataset,
  selectedIds,
  staticViews,
  activeStaticView,
  pending = false,
  onAdd,
  onCreate,
  onRemove,
  onFollow,
  onOpenView,
  onSelectionChange,
  addOpen: addOpenProp,
  onAddOpenChange,
  createOpen: createOpenProp,
  onCreateOpenChange,
  extraActions,
}: ViewSelectionToolbarProps) {
  const [uncontrolledAddOpen, setUncontrolledAddOpen] = React.useState(false);
  const [uncontrolledCreateOpen, setUncontrolledCreateOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [createError, setCreateError] = React.useState<string | null>(null);
  // Covers the full add/remove/create operation, including multi-chunk gaps where
  // react-query's per-mutateAsync isPending briefly drops between chunks.
  const [operationPending, setOperationPending] = React.useState(false);

  const addOpen = addOpenProp ?? uncontrolledAddOpen;
  const setAddOpen = onAddOpenChange ?? setUncontrolledAddOpen;
  const createOpen = createOpenProp ?? uncontrolledCreateOpen;
  const setCreateOpen = onCreateOpenChange ?? setUncontrolledCreateOpen;

  const plural = DATASET_SELECTION_PLURAL[dataset];
  const count = selectedIds.length;
  const isPending = pending || operationPending;

  if (count === 0) return null;

  const applyOutcomes = (
    outcomes: readonly MembershipOutcome[],
    operation: 'add' | 'create' | 'remove',
  ) => {
    onSelectionChange(reconcileViewSelection(selectedIds, outcomes, operation));
  };

  const reportAddOrCreate = (view: WorkspaceView, outcomes: readonly MembershipOutcome[]) => {
    const { succeeded, failed } = countOutcomes(outcomes);
    if (failed === 0) {
      toast.success(membershipSuccessMessage(outcomes, plural, view.name), {
        action: { label: 'Open View', onClick: () => onOpenView(view) },
      });
      return;
    }
    if (succeeded === 0) {
      toast.error(
        membershipOutcomeMessage(outcomes, `Couldn't update the View — please try again.`),
      );
      return;
    }
    // Partial success is still success for the rows that landed, so it offers the same
    // way in as a clean run. Add and create only differed here by the missing action.
    toast.warning(`${succeeded} added, ${failed} could not be added.`, {
      action: { label: 'Open View', onClick: () => onOpenView(view) },
    });
  };

  const add = async (view: WorkspaceView) => {
    setAddOpen(false);
    setOperationPending(true);
    try {
      const outcomes = await onAdd(view.id);
      applyOutcomes(outcomes, 'add');
      reportAddOrCreate(view, outcomes);
    } catch {
      // Task 10 mutations already toast transport/write failures.
      // Chunked handlers return aggregated outcomes instead of throwing mid-batch.
    } finally {
      setOperationPending(false);
    }
  };

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreateError(null);
    setOperationPending(true);
    try {
      const { view, outcomes } = await onCreate(trimmed);
      applyOutcomes(outcomes, 'create');
      const { succeeded } = countOutcomes(outcomes);
      if (succeeded === 0) {
        setCreateError(
          membershipOutcomeMessage(outcomes, "Couldn't create the View — please try again."),
        );
        return;
      }
      setCreateOpen(false);
      setName('');
      reportAddOrCreate(view, outcomes);
    } catch (err) {
      // Keep the dialog open; mutations toast transport failures.
      setCreateError(viewErrorMessage(err, "Couldn't create the View — please try again."));
    } finally {
      setOperationPending(false);
    }
  };

  const followLabel = dataset === 'contact' ? undefined : SYSTEM_VIEWS[dataset].name;

  const follow = async () => {
    if (!onFollow) return;
    setAddOpen(false);
    setOperationPending(true);
    try {
      const { view, outcomes } = await onFollow();
      applyOutcomes(outcomes, 'add');
      if (view) {
        reportAddOrCreate(view, outcomes);
      } else {
        toast.error(membershipOutcomeMessage(outcomes, "Couldn't follow — please try again."));
      }
    } catch {
      toast.error("Couldn't follow — please try again.");
    } finally {
      setOperationPending(false);
    }
  };

  const reportRemove = (view: WorkspaceView, outcomes: readonly MembershipOutcome[]) => {
    const { succeeded, failed } = countOutcomes(outcomes);
    if (failed === 0) {
      toast.success(membershipRemovalMessage(outcomes, plural, view.name));
      return;
    }
    if (succeeded === 0) {
      toast.error(
        membershipOutcomeMessage(outcomes, `Couldn't update the View — please try again.`),
      );
      return;
    }
    toast.warning(`${succeeded} removed, ${failed} could not be removed.`);
  };

  const remove = async () => {
    if (!onRemove || !activeStaticView) return;
    setOperationPending(true);
    try {
      const outcomes = await onRemove();
      applyOutcomes(outcomes, 'remove');
      reportRemove(activeStaticView, outcomes);
    } catch {
      // Task 10 mutations already toast transport/write failures.
      // Chunked handlers return aggregated outcomes instead of throwing mid-batch.
    } finally {
      setOperationPending(false);
    }
  };

  return (
    // Selection is a mode: this bar replaces the whole toolbar row (search,
    // Export, Columns) rather than squeezing in beside it. See the data tables.
    <div className="flex min-w-0 items-center gap-2" data-testid="view-selection-toolbar">
      <Badge variant="secondary" className="whitespace-nowrap font-normal">
        {count} selected
      </Badge>
      <Separator orientation="vertical" className="mx-1 h-5" />

      <Popover open={addOpen} onOpenChange={setAddOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={isPending}>
            Add to View
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <ViewSelectionAddCommand
            staticViews={staticViews}
            pending={isPending}
            onSelectView={(view) => void add(view)}
            followLabel={followLabel}
            onFollow={onFollow ? () => void follow() : undefined}
            onCreateNew={() => {
              setAddOpen(false);
              setCreateError(null);
              setCreateOpen(true);
            }}
          />
        </PopoverContent>
      </Popover>

      {activeStaticView && onRemove ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isPending}
          onClick={() => void remove()}
        >
          Remove from this View
        </Button>
      ) : null}

      {extraActions}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={isPending}
        onClick={() => onSelectionChange([])}
      >
        <X className="size-4" />
        Clear
      </Button>

      <Dialog
        open={createOpen}
        onOpenChange={(next) => {
          if (isPending) return;
          setCreateOpen(next);
          if (!next) {
            setName('');
            setCreateError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <CreateStaticViewDialogBody
            dataset={dataset}
            selectedCount={count}
            name={name}
            onNameChange={setName}
            pending={isPending}
            error={createError}
            onCancel={() => {
              setCreateOpen(false);
              setName('');
              setCreateError(null);
            }}
            onSubmit={() => void create()}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
